import {
  tenantApiRoutes,
  type CreateRoleDto,
  type UpdateRoleDto,
  type UpdateRolePermissionsDto,
  type RoleResponse,
  type PermissionResponse,
} from '@repo/contracts/tenant-api';

import { tenantApi } from '@/redux/api/tenant-api';

export const rolesApi = tenantApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getAllRoles: builder.query<RoleResponse[], void>({
      query: () => tenantApiRoutes.roles.list(),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Role' as const, id })),
              { type: 'Role', id: 'LIST' },
            ]
          : [{ type: 'Role', id: 'LIST' }],
    }),

    getRoleById: builder.query<RoleResponse, string>({
      query: (id) => tenantApiRoutes.roles.getById(id),
      providesTags: (result, error, id) => [{ type: 'Role', id }],
    }),

    createRole: builder.mutation<RoleResponse, CreateRoleDto>({
      query: (data) => ({
        url: tenantApiRoutes.roles.create(),
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Role', id: 'LIST' }],
    }),

    updateRole: builder.mutation<
      RoleResponse,
      { id: string; data: UpdateRoleDto }
    >({
      query: ({ id, data }) => ({
        url: tenantApiRoutes.roles.update(id),
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Role', id },
        { type: 'Role', id: 'LIST' },
      ],
    }),

    deleteRole: builder.mutation<void, string>({
      query: (id) => ({
        url: tenantApiRoutes.roles.delete(id),
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Role', id: 'LIST' }],
    }),

    getRolePermissions: builder.query<PermissionResponse[], string>({
      query: (id) => tenantApiRoutes.roles.permissions(id),
      providesTags: (result, error, id) => [{ type: 'Role', id }],
    }),

    setRolePermissions: builder.mutation<
      RoleResponse,
      { id: string; data: UpdateRolePermissionsDto }
    >({
      query: ({ id, data }) => ({
        url: tenantApiRoutes.roles.permissions(id),
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Role', id },
        { type: 'Role', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetAllRolesQuery,
  useGetRoleByIdQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetRolePermissionsQuery,
  useSetRolePermissionsMutation,
} = rolesApi;
